import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Users, MessageSquare, BarChart3, Download, Mail, RefreshCw,
  AlertCircle, TrendingUp, Calendar, Search, ChevronDown, ChevronUp, ExternalLink, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function StatCard({ icon: Icon, label, value, sub, color }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    rose: 'bg-rose-100 text-rose-600',
    amber: 'bg-amber-100 text-amber-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className="bg-white border border-slate-200 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center ${colors[color]}`}>
          <Icon size={20} />
        </div>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function MiniChart({ data }) {
  if (!data || data.length === 0) return <p className="text-xs text-slate-400 py-4 text-center">Sin datos</p>;

  const max = Math.max(...data.map(d => d.count), 1);
  const barCount = data.length;

  return (
    <div className="flex items-end gap-1 h-24" data-testid="leads-chart">
      {data.slice(-14).map((d, i) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full bg-rose-500 rounded-t transition-all min-h-[2px]"
            style={{ height: `${(d.count / max) * 100}%` }}
            title={`${d.date}: ${d.count} leads`}
          />
          {barCount <= 14 && (
            <span className="text-[8px] text-slate-400 rotate-[-45deg] origin-top-left whitespace-nowrap">
              {d.date.slice(5)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, isAuthenticated, login } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard-stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/leads`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchData();
    }
  }, [isAuthenticated, user]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/leads/export-csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al exportar CSV');
    } finally {
      setExporting(false);
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filteredLeads = useMemo(() => {
    let result = [...leads];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(l =>
        l.nombre?.toLowerCase().includes(term) ||
        l.email?.toLowerCase().includes(term) ||
        l.empresa?.toLowerCase().includes(term) ||
        l.cargo?.toLowerCase().includes(term)
      );
    }
    result.sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [leads, searchTerm, sortField, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      await login(loginEmail, loginPassword);
    } catch (err) {
      setLoginError('Credenciales incorrectas');
    } finally {
      setLoginLoading(false);
    }
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 p-8 max-w-sm w-full">
          <h1 className="text-xl font-bold text-slate-900 mb-1 text-center">Panel de administración</h1>
          <p className="text-sm text-slate-500 mb-6 text-center">Acceso restringido</p>
          {loginError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 p-3 mb-4">{loginError}</p>}
          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Email</label>
              <input type="email" required value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                data-testid="admin-login-email" placeholder="admin@..." />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Contraseña</label>
              <input type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:border-rose-500 outline-none text-sm"
                data-testid="admin-login-password" placeholder="********" />
            </div>
            <button type="submit" disabled={loginLoading}
              className="w-full py-3 bg-slate-900 text-white font-bold hover:bg-black transition disabled:opacity-50"
              data-testid="admin-login-submit">
              {loginLoading ? 'Accediendo...' : 'Acceder'}
            </button>
          </form>
          <Link to="/" className="block text-center text-sm text-slate-500 hover:text-rose-600 mt-4">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft size={18} />
              <span className="text-sm">Inicio</span>
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="font-bold text-slate-900">Panel de Administración</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/chat"
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 transition"
              data-testid="link-admin-chat"
            >
              <MessageSquare size={16} />
              Chat IA
            </Link>
            <Link
              to="/admin/articles"
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 transition"
              data-testid="link-admin-articles"
            >
              <FileText size={16} />
              Posts
            </Link>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 transition text-sm"
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} />
              Actualizar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="stats-grid">
                <StatCard icon={Users} label="Total leads" value={stats.leads.total} sub={`+${stats.leads.today} hoy`} color="rose" />
                <StatCard icon={Calendar} label="Esta semana" value={stats.leads.this_week} color="blue" />
                <StatCard icon={TrendingUp} label="Este mes" value={stats.leads.this_month} color="green" />
                <StatCard icon={MessageSquare} label="Sesiones chat" value={stats.chat.total_sessions} sub={`${stats.chat.total_messages} msgs`} color="amber" />
                <StatCard icon={Mail} label="Contactos" value={stats.contacts.total} sub={`+${stats.contacts.today} hoy`} color="slate" />
              </div>
            )}

            {/* Chart + Sources */}
            {stats && (
              <div className="grid lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white border border-slate-200 p-6">
                  <h2 className="text-sm font-bold text-slate-900 mb-4">Leads últimos 14 días</h2>
                  <MiniChart data={stats.leads.per_day} />
                </div>
                <div className="bg-white border border-slate-200 p-6">
                  <h2 className="text-sm font-bold text-slate-900 mb-4">Leads por origen</h2>
                  {stats.leads.by_source.length > 0 ? (
                    <div className="space-y-3">
                      {stats.leads.by_source.map((s) => (
                        <div key={s.source} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{s.source}</span>
                          <span className="text-sm font-bold text-slate-900">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Sin datos</p>
                  )}
                </div>
              </div>
            )}

            {/* Leads Table */}
            <div className="bg-white border border-slate-200" data-testid="leads-table-container">
              <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h2 className="font-bold text-slate-900">Leads</h2>
                  <p className="text-xs text-slate-500">{filteredLeads.length} de {leads.length} registros</p>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <div className="relative flex-1 sm:flex-none">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Buscar nombre, email, empresa..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-4 py-2 border border-slate-200 text-sm w-full sm:w-64 focus:outline-none focus:border-rose-500"
                      data-testid="leads-search"
                    />
                  </div>
                  <button
                    onClick={handleExportCSV}
                    disabled={exporting || leads.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition disabled:opacity-50 whitespace-nowrap"
                    data-testid="export-csv-btn"
                  >
                    <Download size={16} />
                    {exporting ? 'Exportando...' : 'Exportar CSV'}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {[
                        { key: 'nombre', label: 'Nombre' },
                        { key: 'email', label: 'Email' },
                        { key: 'cargo', label: 'Cargo' },
                        { key: 'empresa', label: 'Empresa' },
                        { key: 'origen', label: 'Origen' },
                        { key: 'created_at', label: 'Fecha' },
                      ].map(col => (
                        <th
                          key={col.key}
                          className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer hover:text-slate-900 select-none"
                          onClick={() => toggleSort(col.key)}
                        >
                          <span className="flex items-center gap-1">
                            {col.label}
                            <SortIcon field={col.key} />
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                          {searchTerm ? 'No se encontraron resultados' : 'No hay leads registrados'}
                        </td>
                      </tr>
                    ) : (
                      filteredLeads.map((lead, i) => (
                        <tr key={lead.id || i} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`lead-row-${i}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{lead.nombre}</td>
                          <td className="px-4 py-3 text-slate-600">{lead.email}</td>
                          <td className="px-4 py-3 text-slate-600">{lead.cargo}</td>
                          <td className="px-4 py-3 text-slate-600">{lead.empresa || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                              {lead.origen}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                            {new Date(lead.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
